import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  InputHTMLAttributes,
} from "react";
import styled, { css } from "styled-components";
import { FLEX } from "styles/flex";
import { COLORS } from "styles/colors";
import Input from "components/atoms/input/Input";
import * as mediaQuery from "components/MediaQuery";
import { checkKeywordSpace } from "utils/functions";
import { IoMdCloseCircle } from "react-icons/io";
import { FaSearch } from "react-icons/fa";
export type ClickSearch = (keyword: string) => void;

export interface SearchInputProps
  extends InputHTMLAttributes<HTMLInputElement> {
  /** 검색 실행(Enter/버튼) */
  onClickSearch: ClickSearch;
  /** validation 에러 스타일 표시 */
  isValidated?: boolean;
  /** 제어 컴포넌트 값 */
  keyword?: string;
  /** 지도 컨텍스트 스타일 */
  map?: boolean;
  /** 값 클리어 콜백 */
  onClear?: () => void;
  /** 디바운스 시간(ms). 0이면 비활성 */
  debounce?: number;
  "data-testid"?: string;
}

const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      isValidated,
      keyword, // 제어값(옵셔널)
      map,
      onClickSearch,
      onClear,
      debounce,
      placeholder = "Search",
      disabled,
      "data-testid": dataTestId = "atoms-input-search-styled-input-change",
      ...rest
    },
    ref
  ) => {
    const [innerValue, setInnerValue] = useState<string>(keyword ?? "");
    const value = innerValue;

    const inputId = useId();
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearTimer = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    useEffect(() => {
      setInnerValue(keyword ?? "");
    }, [keyword]);

    useEffect(() => {
      return () => clearTimer();
    }, []);

    // 타이핑 시 즉시 콜백 + 내부 상태 갱신
    const handleChange = useCallback(
      (next: string, e?: React.ChangeEvent<HTMLInputElement>) => {
        if (checkKeywordSpace(next)) {
          return;
        }

        setInnerValue(next);

        if (debounce) {
          clearTimer();
          timerRef.current = setTimeout(() => {
            if (rest.onChange && e) {
              rest.onChange(e);
            }

            !map && onClickSearch(next);
          }, debounce);
        }
      },

      [rest.onChange, debounce, map, onClickSearch]
    );
    const doSearch = useCallback(() => {
      if (checkKeywordSpace(value) || disabled) {
        return;
      }
      clearTimer();
      onClickSearch(value);
    }, [onClickSearch, value, disabled]);

    const onClearValue = useCallback(() => {
      clearTimer();
      handleChange("");
      if (!map) {
        onClickSearch("");
      }
      onClear?.();
    }, [handleChange, onClickSearch]);

    return (
      <Wrapper
        role="search"
        aria-label="search-input"
        map={map}
        isValidated={isValidated}
        disabled={disabled}
        className="search-input-wrapper"
      >
        <StyledInput
          {...rest}
          type="search"
          map={map}
          id={inputId}
          ref={ref}
          value={value}
          isValidated={isValidated}
          aria-invalid={!!isValidated}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              doSearch();
            }
          }}
          onChange={(e) => handleChange(e.target.value, e)}
          autoComplete="off"
          placeholder={placeholder}
          data-testid={dataTestId}
        />

        <ClearButton
          type="button"
          aria-label="search-delete"
          onClick={onClearValue}
          isVisible={!!value && !disabled}
          data-testid={`${dataTestId}-clear-button`}
        >
          <IoMdCloseCircle color={COLORS.LIGHTGRAY} size={14} />
        </ClearButton>

        <SearchButton
          type="button"
          aria-label="search-button"
          disabled={disabled}
          onClick={doSearch}
          data-testid={`${dataTestId}-search-button`}
        >
          <FaSearch
            className="search-input-icon"
            size={16}
            color={COLORS.LIGHTGRAY}
          />
        </SearchButton>
      </Wrapper>
    );
  }
);

export default SearchInput;

/* ===================== styles ===================== */

const Wrapper = styled.div<{
  map?: boolean;
  isValidated?: boolean;
  disabled?: boolean;
}>`
  ${FLEX.FlexBetweenCenter};
  background-color: ${COLORS.WHITE};
  border-radius: 2px;
  flex: 2;
  border: 1px solid #abb5c5;
  max-width: 100%;
  width: 100%;
  position: relative;

  ${({ isValidated }) =>
    isValidated &&
    css`
      border: 1px solid #ff2e2e;
      background-color: #ffd5d5;
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      background-color: ${COLORS.BUTTONS1};
      cursor: not-allowed;
      opacity: 0.7;
      pointer-events: none;
    `}

  ${({ map, theme }) =>
    map &&
    css`
      width: 250px;
      max-width: 250px;
      background-color: ${theme.COLORS.FILTERITEM};
      border-radius: 4px;
      overflow: hidden;
      border: 1px solid #abb5c5;
    `}
`;

const StyledInput = styled(Input)<{ isValidated?: boolean; map?: boolean }>`
  border: none;
  appearance: none;
  background: none;

  ${({ map, theme }) =>
    map &&
    css`
      background-color: ${theme.COLORS.FILTERITEM};
      padding: 8px 10px;
      color: ${theme.COLORS.WHITE};
      line-height: 1;

      ::placeholder {
        color: #abb5c5;
      }

      ${mediaQuery.isMobile} {
        padding: 10px;
      }
    `}
`;

const Button = styled.button`
  ${FLEX.FlexCenterCenter};
  padding: 0 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${COLORS.LIGHTGRAY};
  height: 100%;
  display: inline-flex;
`;
const SearchButton = styled(Button)``;

const ClearButton = styled(Button)<{ isVisible: boolean }>`
  padding: 0;
  flex-shrink: 0;

  visibility: ${({ isVisible }) => (isVisible ? "visible" : "hidden")};
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transition: opacity 0.15s ease;
`;

/* ===================== 사용 팁 ===================== */
// - 제어형: <SearchInput keyword={state} onChangeKeyword={setState} />
// - 비제어형: <SearchInput onDebouncedChange={(v)=>fetch(v)} />
// - 최종 검색 트리거: onClickSearch(Enter/버튼)에서 API 호출
